import { useSyncExternalStore } from 'react';
import { getKsdmConfig } from 'helpers/index';

/**
 * `footer#ksdm-footer`, CMS Admin variant — `templates/common/CmsBase.html:22-30`.
 *
 * Unlike the sizer / partners / user-settings shells this one has BOTH the
 * `container-fluid` class on `<footer>` AND a `col-xs-10 col-xs-offset-2`
 * wrapper that clears the 2-column left nav. That wrapper is the element
 * `cms.main.js:move_footer` (:100-108) mutates when `#left_resizer` toggles:
 * it swaps `col-xs-10 col-xs-offset-2` for `to-left`. `useCmsFormControls`
 * reproduces that mutation, so the classes below are the INITIAL state only.
 *
 * The body is the shared `common/footer.html` include, same as every other page.
 */

/* ═══════════════════════════════════════════════════════════════════════════
 * ★ Phase 7 · T2 — the CMS version label is WRITABLE, because a screen writes it.
 *
 * `manufacturer/view/confirm_popup.js:35-44` ends a CMS-version update with
 *     $('footer .cms_version').text(date.replace(/-/g,'') + '.' + new_value)
 * i.e. it reaches into THIS footer's DOM. MEASURED on `:8130` 2026-08-05:
 * the span went `"C.20250205.027 "` → `"20250205.028"` — the `C.` prefix and
 * the trailing space are LOST, and the date is the one the page booted with,
 * not the one the server just stamped. That is a legacy bug, and a parity port
 * reproduces it; what it must not do is let a CMS screen mutate another
 * component's DOM. So the value lives here and `setCmsFooterVersion` publishes
 * changes, exactly as `app/mfgProfile.ts` does for the header's logo/initials.
 * Reported as a shell change, per the T2 brief.
 * ═══════════════════════════════════════════════════════════════════════════ */

type Listener = () => void;
const versionListeners = new Set<Listener>();

/**
 * The span's ENTIRE text content, not just the version token — because
 * `.text(version)` replaces all of it. `common/footer.html` renders the boot
 * value with a trailing space (measured `"C.20250205.027 "`), and the update
 * measured on `:8130` has none (`"20250205.028"`), so the space belongs to the
 * value rather than to the JSX.
 */
let cmsVersionLabel: string | undefined = (() => {
  const boot = getKsdmConfig()?.versions?.cms;
  return boot === undefined ? undefined : `${boot} `;
})();

/** Replace the `<span class="cms_version">` text, as `confirm_popup.done` does. */
export const setCmsFooterVersion = (value: string): void => {
  if (value === cmsVersionLabel) return;
  cmsVersionLabel = value;
  versionListeners.forEach((listener) => listener());
};

const subscribeVersion = (listener: Listener): (() => void) => {
  versionListeners.add(listener);
  return () => {
    versionListeners.delete(listener);
  };
};

const getVersionSnapshot = (): string | undefined => cmsVersionLabel;

const CmsFooter = () => {
  const cfg = getKsdmConfig();
  const year = cfg?.year ?? new Date().getFullYear();
  const software = cfg?.versions?.software;
  const master = cfg?.versions?.master;
  const cmsVersion = useSyncExternalStore(
    subscribeVersion,
    getVersionSnapshot,
    getVersionSnapshot
  );

  return (
    <footer id="ksdm-footer" className="container-fluid">
      <div className="col-xs-10 col-xs-offset-2">
        <div className="col-xs-12">
          <div className="text-right">
            <small id="ksdm-copyright" className="right mt-0">
              Powered by Kinetix Air | &#169; {year} Kinetix Software LLC | All Rights Reserved
            </small>
          </div>
          {software && (
            /*
             * ★ `display: 'inline'` is a LEAK REPAIR, not a style choice.
             *
             * `pages/dashboard/components/PageFooter.module.scss:37` ships
             *     :global(#ksdm-footer #ksdm-dbs-version) { display: inline-block; … }
             * which lands in the shared `assets/main-*.css` and therefore applies
             * on EVERY React route — including this one, which does not use
             * `PageFooter` at all. Legacy computes `display: inline` here, and
             * the difference is visible: an inline-block establishes its own box,
             * so the version string stops baseline-shifting inside the 14px line
             * box and rides 3px too high, taking `#ksdm-footer` 1px with it.
             * Measured old y=978 h=15.0 / footer h=20 vs new y=974.3 h=15.7 /
             * footer h=20.7. Same failure class as the `StatusesRail.module.scss`
             * leak repaired in `CmsAdminPage`. Fixed route-locally rather than in
             * the shared module, which the other pages depend on.
             *
             * The whitespace text nodes below simply mirror `common/footer.html`
             * (an HTML template keeps its newlines); they are cosmetic — measured
             * before and after, they change no geometry.
             */
            <div className="">
              {' '}
              <small id="ksdm-dbs-version" className="mt-0" style={{ display: 'inline' }}>
                {' '}
                <span className="ka-has-tooltip" tooltip="Software version">
                  {software}
                </span>{' '}
                |{' '}
                <span className="ka-has-tooltip" tooltip="Master DB version">
                  {master}
                </span>{' '}
                |{' '}
                {/* ★ T2: the trailing space now lives INSIDE the stored value
                    (see `cmsVersionLabel`), so the text content is unchanged at
                    boot and matches legacy exactly after a version update, when
                    `.text(...)` drops it. */}
                <span className="ka-has-tooltip cms_version" tooltip="CMS DB version">
                  {cmsVersion}
                </span>{' '}
              </small>{' '}
            </div>
          )}
        </div>
      </div>
    </footer>
  );
};

export default CmsFooter;
